import { http } from '@/lib/http';
import { WORKDAY_INTEGRATION_TYPE } from './constants';
import type {
  CreateHirePayload,
  CreateHireResult,
  HireFormResponse,
} from './types';
import type { KomboEnvelope } from '../workdayPosition/types';

// `@/lib/http` is the `/api/v1` instance every other Kombo call uses; its
// interceptor already unwraps to the `{ data, errors }` envelope.

/**
 * Workday's own hire form. Same schema shape as Kombo's employee form, so the
 * existing `convertSchemaToFormConfig` renders it unchanged.
 */
const getHireForm = async (
  atsApplicationId: string | number,
  /**
   * Asks for the rehire shape: the candidate's name, email and phone are
   * replaced by a single `Applicant_Reference` select, because Workday already
   * holds them.
   */
  rehire = false
): Promise<KomboEnvelope<HireFormResponse>> =>
  http.get('kombo/hire_form', {
    params: {
      integration_type: WORKDAY_INTEGRATION_TYPE,
      ats_application_id: atsApplicationId,
      ...(rehire ? { rehire: true } : {}),
    },
  });

/**
 * Completes the hire in Workday. Requires a position already created for the
 * application — without one it answers 422 `err_workday_position_missing`.
 *
 * Not repeatable. A repeat POST returns the stored state rather than sending
 * anything, so the backend is safe, but the UI must never invite a second
 * attempt for anything but a validation rejection.
 */
const createHire = async (
  payload: Omit<CreateHirePayload, 'integration_type'>
): Promise<KomboEnvelope<CreateHireResult>> =>
  http.post('kombo/create_hire', {
    integration_type: WORKDAY_INTEGRATION_TYPE,
    ...payload,
  });

/**
 * Finds a Workday applicant by id or email.
 *
 * A lookup, not a list: the applicant collection is ~4,200 rows and ~44MB, and
 * fetching it to identify one person timed out. Workday's applicant API has no
 * name filter at all — worker reference, former worker reference, email or a
 * custom-field construct — so a name returns nothing, which is why the field
 * carries a hint.
 *
 * `link` comes from the schema rather than being built here, so a tenant on a
 * different path still works.
 */
const applicantLookup = async (
  link: string,
  query: string
): Promise<KomboEnvelope<{ results?: { id: string; name?: string }[] }>> =>
  // The link already carries `/api/v1`, which `@/lib/http` also prefixes.
  http.get(link.replace(/^\/api\/v1/, ''), {
    params: { integration_type: WORKDAY_INTEGRATION_TYPE, query },
  });

export const WORKDAY_HIRE_API = { getHireForm, createHire, applicantLookup };
